import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Clapperboard, Share2, Link2, Instagram, Lock, Loader2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37", RED = "#C41E24";
const fmt = (d) => { const t = new Date(d); return isNaN(t) ? d : t.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); };

const ShareMenu = ({ h, token }) => {
  const [busy, setBusy] = useState("");
  const link = `${window.location.origin}/highlight/${h.share_token}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied", { description: "Paste it anywhere — TikTok, X, texts, team chat." }); }
    catch { window.prompt("Copy your highlight link:", link); }
  };
  const instagram = async () => {
    setBusy("ig");
    try {
      const res = await fetch(`${API}${h.video_url}?auth=${token}`);
      const blob = await res.blob();
      const file = new File([blob], `nexto-highlight-${h.id.slice(0, 6)}.mp4`, { type: blob.type || "video/mp4" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: h.title, text: `${h.title} · The Next O Technical Academy ${link}` });
      } else {
        await navigator.clipboard?.writeText(link).catch(() => {});
        toast.info("Link copied — opening Instagram", { description: "Instagram doesn't accept web uploads here: save the video from the link, then post it in the app." });
        window.open("https://www.instagram.com/", "_blank", "noopener");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open the share sheet."); }
    setBusy("");
  };
  return (
    <div data-testid={`highlight-share-${h.id}`} className="flex flex-wrap items-center gap-2">
      <button data-testid={`highlight-share-ig-${h.id}`} onClick={instagram} disabled={!!busy} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm text-white disabled:opacity-70" style={{ background: "linear-gradient(135deg,#f58529,#dd2a7b 55%,#8134af)" }}>
        {busy === "ig" ? <Loader2 size={12} className="animate-spin" /> : <Instagram size={12} />} Instagram
      </button>
      <button data-testid={`highlight-share-copy-${h.id}`} onClick={copy} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm border text-white/80 hover:text-white" style={{ borderColor: `${GOLD}66` }}>
        <Link2 size={12} style={{ color: GOLD }} /> Copy Link
      </button>
      <a data-testid={`highlight-share-open-${h.id}`} href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/45 hover:text-white"><Share2 size={11} /> Preview</a>
    </div>
  );
};

// Player Hub "My Highlights" tab — coach-posted session highlights (separate from homework proofs), share gated by parent consent.
export default function MyHighlights({ token, data, onSeen }) {
  const navigate = useNavigate();
  if (!data) return <div data-testid="highlights-loading" className="text-sm text-white/40">Loading highlights…</div>;
  const { highlights, share_consent } = data;
  const markSeen = (h) => { if (h.seen) return; axios.post(`${API}/highlights/${h.id}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {}); onSeen?.(h.id); };
  return (
    <div data-testid="my-highlights">
      {!share_consent && (
        <div data-testid="highlights-consent-notice" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm" style={{ borderColor: `${RED}66`, background: `${RED}12` }}>
          <Lock size={15} style={{ color: "#ff8a8e" }} />
          <span className="text-white/80 flex-1 min-w-[200px]">Sharing is off. A parent or guardian must confirm consent before highlights can be shared outside the Hub.</span>
          <button data-testid="highlights-consent-link" onClick={() => navigate("/profile")} className="text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm text-black" style={{ background: GOLD }}>Parent Consent Settings</button>
        </div>
      )}
      {highlights.length === 0 ? (
        <div data-testid="highlights-empty" className="rounded-lg border border-dashed border-white/15 p-8 text-center">
          <Clapperboard size={28} className="mx-auto mb-3" style={{ color: GOLD }} />
          <div className="font-headings text-lg uppercase tracking-wide">No highlights yet</div>
          <p className="text-sm text-white/45 mt-1">When Coach Barker records a highlight at one of your sessions, it lands here — ready to share.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {highlights.map((h, i) => (
            <motion.div key={h.id} data-testid={`highlight-card-${h.id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 6) * 0.05 }}
              className="rounded-xl border overflow-hidden relative" style={{ borderColor: h.seen ? "rgba(255,255,255,0.1)" : `${GOLD}99`, background: "#111", boxShadow: h.seen ? "none" : `0 0 24px ${GOLD}33` }}>
              {!h.seen && <span data-testid={`highlight-new-${h.id}`} className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded text-black" style={{ background: GOLD }}><Sparkles size={9} /> New</span>}
              <video data-testid={`highlight-video-${h.id}`} src={`${API}${h.video_url}?auth=${token}`} preload="metadata" controls playsInline onPlay={() => markSeen(h)} className="w-full aspect-video bg-black" />
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><div className="font-headings text-lg uppercase tracking-wide truncate">{h.title}</div><div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">{fmt(h.created_at)} · Coach Barker</div></div>
                  <Clapperboard size={16} className="shrink-0 mt-1" style={{ color: GOLD }} />
                </div>
                {h.note && <p className="text-sm text-white/65 italic mt-2">“{h.note}”</p>}
                <div className="mt-3">{h.visibility && h.visibility !== "public" ? <span data-testid={`highlight-share-private-${h.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45"><Lock size={11} /> {h.visibility === "team" ? "Shared with your team" : "Private — only you can see this"}</span> : share_consent ? <ShareMenu h={h} token={token} /> : <span data-testid={`highlight-share-locked-${h.id}`} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/35"><Lock size={11} /> Sharing locked · parent consent required</span>}</div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
